import { useEffect, useState } from "react";
import { incentiveClaimSections } from "../../forms/incentiveClaimApplicationFields";
import Button from "../common/Button";
import IncentiveClaimFormSection from "./IncentiveClaimFormSection";
import IncentiveClaimFormSubSection from "./IncentiveClaimFormSubSection";
import {
  formatDate,
  getKeyByValue,
  numberToWords,
  scriptRegex,
  validateMultiSectionFormData,
  validateSingleSectionFormData,
} from "../../utils/utils";
import api from "../../api/ApiService";
import { getStoredItem } from "../../config/storageUtils";
import {
  allNicOptions,
  amountFieldsToConvertToWords,
  calculateEmploymentFields,
  calculationFields,
  capitalSubsidySecondInstallmentDocuments,
  documentTypeMap,
  employmentFields,
  financeCheckboxes,
  infoTexts,
  insertAssistanceDocuments,
  insterstSubsidyBankDetailsFieldsFirstInstallment,
  insterstSubsidyBankDetailsFieldsSecondInstallment,
  insterstSubsidyBankDetailsFieldsThirdInstallment,
  LAND_TYPES,
  landFieldGroups,
  maxEligibleSubsidy,
  nicCodeToTourismProducts,
  shareholdingTables,
  sourceFinanceCheckBoxTargets,
  sourceFinanceTableAmountTargets,
  sourceofFinanceTitle,
  totalInvestmentFields,
  totalNonEligibleFields,
} from "../../config/incentiveClaimFormUtils";
import { useNavigate, useParams } from "react-router-dom";
import { CheckIcon } from "../../icons/CommonIcons";
import LoaderOverlay from "../common/LoaderOverlay";
import {
  applicationStatusMap,
  incentiveClaimTypes,
} from "../../config/applicationStatusConfig";
import {
  installmentsTypes,
  schemeTypes,
} from "../../config/appliedSchemesConfig";
import { uttarakhandDistrictList } from "../../config/districtConfig";
import { useLanguage } from "../LanguageProvider/LanguageProvider";
import Message from "../common/Message";

const IncentiveClaimForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const { applicationId } = useParams();
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [financialIncentiveClaim, setFinancialIncentiveClaim] = useState(null);
  const [installmentType, setInstallmentType] = useState(null);
  const landTypeDynamicFields = landFieldGroups.all;
  const navigate = useNavigate();
  const [tourismOptions, setTourismOptions] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [declarationError, setDeclarationError] = useState("");
  const [loadingPDE, setLoadingPDE] = useState(false);
  const [nicOptions, setNicOptions] = useState(allNicOptions);
  const [pendingDocIds, setPendingDocIds] = useState([]);
  const [tehsilMaster, setTehsilMaster] = useState([]);
  const [tehsilOptions, setTehsilOptions] = useState([]);
  const { language, setLanguage } = useLanguage();
  const [apiMessage, setApiMessage] = useState(null);

  useEffect(() => {
    const getUserDetails = async () => {
      const resp = await api.get("/user-full-data");
      if (resp.status === 200) {
        setUserDetails(resp.data);
      }
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appDetails, appRes, docRes, tehsilsRes] = await Promise.all([
          api.get(`/get-application/${applicationId}`),
          api.get(`/get-caf/${applicationId}`),
          api.post(`/sub-categories/documents`, {
            category_id: 1,
            application_id: applicationId,
            user_id: userDetails?.user_details?.id,
          }),
          api.get("/uttarakhand/districts/tehsils"),
        ]);

        // Use API data if it exists, otherwise fallback to userDetails
        const appData =
          appRes?.data &&
          typeof appRes.data === "object" &&
          Object.keys(appRes.data).length > 0
            ? appRes.data
            : {
                caf_id: userDetails?.caf_id || "",
                approval_date: userDetails?.application_date || "",
                unit_name: userDetails?.unit_name || "",
                unit_address: userDetails?.unit_address || "",
                contact_no: userDetails?.mobile_number || "",
                investor_details: userDetails?.investor_details || "",
              };

        const newStatus = appDetails.status || null;
        const newClaim = appDetails.invest_claim_id || null;
        const newInstallment = appDetails.application_type || null;

        setApplicationStatus(newStatus);
        setFinancialIncentiveClaim(newClaim);
        setInstallmentType(newInstallment);

        // Process document data
        const docData = {};
        docRes?.data?.forEach((doc) => {
          const docTypeId = doc?.id;
          const fieldName = getKeyByValue(documentTypeMap, doc?.name_en);
          if (doc.upload_documents?.length > 0) {
            const uploaded = doc.upload_documents[0];
            docData[fieldName] = {
              url: uploaded.path,
              id: uploaded.id,
              name: uploaded.filename || "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          } else {
            docData[fieldName] = {
              url: "",
              id: "",
              name: "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          }
        });

        let maxAmount = 0;

        if (appData.area_category === "A") {
          maxAmount = 400000;
        } else if (appData.area_category === "B") {
          maxAmount = 500000;
        } else if (appData.area_category === "C") {
          maxAmount = 600000;
        }

        let initialData = {
          ...appData,
          ...docData,
          maximum_eligible_subsidy_investment: maxEligibleSubsidy,
          maximum_eligible_amount_first_installment: maxAmount,
          maximum_eligible_amount_second_installment: maxAmount,
          maximum_eligible_amount_third_installment: maxAmount,
          investment_certificate: {
            name: "InvestmentCertificate.pdf",
            id: "",
            url: userDetails?.certificate_url || "",
            label: "Investment Certificate",
          },
        };

        // 🔹 Ensure words are pre-filled on initial load
        amountFieldsToConvertToWords.forEach((field) => {
          if (initialData[field]) {
            initialData[`${field}_in_words`] = numberToWords(
              initialData[field],
            );
          }
        });

        initialData[`maximum_eligible_subsidy_investment_in_words`] =
          numberToWords(maxEligibleSubsidy);

        setFormData(initialData);

        // Tehsil options
        setTehsilMaster(tehsilsRes.data || []);

        if (!validateFinanceCheckboxes(initialData)) {
          setErrors((prev) => ({
            ...prev,
            term_loan: {
              en: "Please select at least one source of finance.",
              hi: "कृपया कम से कम एक वित्त स्रोत चुनें।",
            },
          }));
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    if (!applicationId || !userDetails?.user_details?.id) return;

    fetchData();
    if (
      applicationStatus === applicationStatusMap.incompleteDocumentPending.value
    ) {
      getPendingDocs();
    }
  }, [applicationId, userDetails?.user_details?.id]);

  const getPendingDocs = async () => {
    try {
      const logsResponse = await api.post("/user/application/logs", {
        application_id: applicationId,
        user_id: userDetails?.user_details?.id,
      });

      if (logsResponse?.data && Array.isArray(logsResponse.data)) {
        const latestLog = logsResponse.data
          .filter(
            (log) =>
              log.status ===
                applicationStatusMap.incompleteDocumentPending.value &&
              log.document_type_id,
          )
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )[0];

        if (latestLog?.document_type_id) {
          const docIds = latestLog.document_type_id
            .split(",")
            .map((id) => id.trim());
          setPendingDocIds(docIds);
        }
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    if (formData.nic_code) {
      const products = nicCodeToTourismProducts[formData.nic_code] || [];
      setTourismOptions(products);
    }
  }, [formData.nic_code]);

  useEffect(() => {
    if (formData?.project_type === "Expansion") {
      setNicOptions([{ label: "55101", value: "55101", hi: "55101" }]);
    } else {
      setNicOptions(allNicOptions);
    }
  }, [formData?.project_type]);

  useEffect(() => {
    if (!formData.land_district || !tehsilMaster.length) return;

    const options = tehsilMaster
      .filter(
        (t) =>
          String(t.district_id) ===
          String(
            uttarakhandDistrictList.find(
              (d) => d.label === formData.land_district,
            )?.id,
          ),
      )
      .map((t) => ({
        label: t.name,
        value: t.name,
        hi: t.name_hi,
      }));

    setTehsilOptions(options);
  }, [formData.land_district, tehsilMaster]);

  const validateFinanceCheckboxes = (data) => {
    return financeCheckboxes.some((field) => {
      const value = data[field];
      return value === true || value === 1 || value === "1";
    });
  };

  // validate current step only
  const handleNext = async () => {
    const { errors: stepErrors, isValid } = validateSingleSectionFormData(
      incentiveClaimSections[currentStep]?.data || [],
      formData,
    );

    const { errors: tableErrors, isValid: tableValid } = validateTableFields(
      incentiveClaimSections[currentStep]?.data || [],
      formData,
    );

    const financeSubSection = incentiveClaimSections[currentStep]?.data?.find(
      (sub) => sub.title.en === sourceofFinanceTitle.en,
    );

    if (financeSubSection && !validateFinanceCheckboxes(formData)) {
      stepErrors.term_loan = {
        en: "Please select at least one source of finance.",
        hi: "कृपया कम से कम एक वित्त स्रोत चुनें।",
      };
    }

    const capitalSubsidySection = incentiveClaimSections[
      currentStep
    ]?.data?.find((sub) => sub.title?.en === "Capital Subsidy");

    if (
      capitalSubsidySection &&
      !formData?.capital_expenditure_details_pdf?.url
    ) {
      stepErrors.capital_expenditure_details_through_bank_transactions = {
        en: "Please upload a valid Expenditure Sheet and PDF file.",
        hi: "कृपया मान्य व्यय विवरण शीट और पीडीएफ फ़ाइल अपलोड करें।",
      };
    }

    // 🔹 Delete skipped errors directly from stepErrors
    Object.keys(stepErrors).forEach((name) => {
      if (
        skipLandTypeErrors(name) ||
        skipFinanceErrors(name, formData) ||
        skipInsertAssistanceDocuments(name) ||
        skipCapitalSubsidySecondInstallmentDocuments(name) ||
        skipGenderValidation(name) ||
        skipInterestSubsidyInstallmentErrors(name) ||
        skipFileExpenditureFileErrors(name)
      ) {
        delete stepErrors[name];
      }
    });

    Object.keys(tableErrors).forEach((name) => {
      if (skipInterestSubsidyInstallmentErrors(name) ||
          skipFileExpenditureFileErrors(name)) {
        delete tableErrors[name];
      }
    });

    const combinedErrors = { ...stepErrors, ...tableErrors };
    const finalValid = Object.keys(combinedErrors).length === 0 && tableValid;

    setErrors((prev) => ({ ...prev, ...combinedErrors }));

    if (finalValid && currentStep < incentiveClaimSections.length - 1) {
      const done = await createApplicationForm(false);
      if (done) {
        if (
          String(financialIncentiveClaim) ===
            schemeTypes.sreimbursementcapitalincentive.value &&
          currentStep === incentiveClaimSections.length - 3
        ) {
          setCurrentStep((prev) => prev + 2);
        } else {
          setCurrentStep((prev) => prev + 1);
        }
      }
    }
  };

  const createApplicationForm = async (isFinalSubmit) => {
    try {
      const filteredFormData = Object.fromEntries(
        Object.entries(formData).filter(
          ([key, value]) =>
            // Exclude file objects that have 'url' or 'id' keys
            !(
              value &&
              typeof value === "object" &&
              ("url" in value || "id" in value)
            ),
        ),
      );

      const submitResponse = await api.post("/applications/formDetails/save", {
        caf_id: userDetails?.caf_id,
        financial_incentive_claim: financialIncentiveClaim,
        category_id: 1,
        user_id: userDetails?.user_details?.id,
        application_id: applicationId,
        ...filteredFormData,
      });

      if (submitResponse.status === 200) {
        if (isFinalSubmit) {
          const statusFormData = new FormData();
          statusFormData.append("status", "8");
          statusFormData.append("application_id", applicationId);
          statusFormData.append("remarks", "Application is submitted");
          statusFormData.append("application_stage", "");
          const changeApplicationStatusResponse = await api.post(
            "/categories/applications/status/change",
            statusFormData,
          );
          if (
            changeApplicationStatusResponse.status === "200" ||
            changeApplicationStatusResponse.status === 200
          ) {
            setIsFormSubmitted(true);
          }
        }
        return true;
      }
      return false;
    } catch (error) {
      scrollTo(0, 0);
      setApiMessage({
        type: "error",
        message: error?.response?.data?.message
          ? error?.response?.data?.message
          : language
            ? "फॉर्म सबमिट करने में त्रुटि"
            : "Error submitting form.",
      });
      return false;
    }
  };

  // validate ALL steps on final submit
  const handleSubmit = () => {
    let { errors: allErrors } = validateMultiSectionFormData(
      incentiveClaimSections,
      formData,
    );

    // also validate tables for all sections
    let tableErrors = {};
    let allTableValid = true;

    incentiveClaimSections.forEach((section) => {
      const { errors: tErr, isValid: tValid } = validateTableFields(
        section.data || [],
        formData,
      );
      tableErrors = { ...tableErrors, ...tErr };
      if (!tValid) allTableValid = false;
    });

    // 🔹 Section-specific: require at least one finance checkbox
    const financeSection = incentiveClaimSections
      .flatMap((section) => section.data) // look inside all subsections
      .find((sub) => sub.title.en === sourceofFinanceTitle.en);

    if (financeSection && !validateFinanceCheckboxes(formData)) {
      allErrors.term_loan = {
        en: "Please select at least one source of finance.",
        hi: "कृपया कम से कम एक वित्त स्रोत चुनें।",
      };
    }

    const capitalSubsidySection = incentiveClaimSections
      .flatMap((section) => section.data) // look inside all subsections
      .find((sub) => sub.title === "Capital Subsidy");

    if (
      capitalSubsidySection &&
      !formData?.capital_expenditure_details_pdf?.url
    ) {
      allErrors.capital_expenditure_details_through_bank_transactions = {
        en: "Please upload a valid Expenditure Sheet and PDF file.",
        hi: "कृपया मान्य व्यय विवरण शीट और पीडीएफ फ़ाइल अपलोड करें।",
      };
    }

    // 🔹 Remove skipped errors (land type dependent)
    Object.keys(allErrors).forEach((name) => {
      if (
        skipLandTypeErrors(name) ||
        skipFinanceErrors(name, formData) ||
        skipInsertAssistanceDocuments(name) ||
        skipCapitalSubsidySecondInstallmentDocuments(name) ||
        skipGenderValidation(name) ||
        skipInterestSubsidyInstallmentErrors(name) ||
        skipFileExpenditureFileErrors(name)
      ) {
        delete allErrors[name];
      }
    });

    Object.keys(tableErrors).forEach((name) => {
      if (
        skipInterestSubsidyInstallmentErrors(name) ||
        skipFileExpenditureFileErrors(name)
      ) {
        delete tableErrors[name];
      }
    });

    // merge errors
    const combinedErrors = { ...allErrors, ...tableErrors };

    // 🔹 recalc validity
    const finalValid = Object.keys(allErrors).length === 0 && allTableValid;

    setErrors(combinedErrors);

    if (!declarationChecked) {
      setDeclarationError({
        en: "You must agree to the declaration before submitting.",
        hi: "सबमिशन से पहले आपको घोषणा से सहमत होना अनिवार्य है।",
      });
      return;
    } else {
      setDeclarationError({ en: "", hi: "" });
    }

    if (!finalValid) {
      // focus first tab with error
      for (let i = 0; i < incentiveClaimSections.length; i++) {
        let { errors: stepErr } = validateSingleSectionFormData(
          incentiveClaimSections[i]?.data || [],
          formData,
        );

        // filter skipped errors here too
        Object.keys(stepErr).forEach((name) => {
          if (
            skipLandTypeErrors(name) ||
            skipFinanceErrors(name, formData) ||
            skipInsertAssistanceDocuments(name) ||
            skipCapitalSubsidySecondInstallmentDocuments(name) ||
            skipGenderValidation(name) ||
            skipInterestSubsidyInstallmentErrors(name) ||
            skipFileExpenditureFileErrors(name)
          ) {
            delete stepErr[name];
          }
        });

        const { errors: tErr } = validateTableFields(
          incentiveClaimSections[i]?.data || [],
          formData,
        );

        Object.keys(tErr).forEach((name) => {
          if (skipInterestSubsidyInstallmentErrors(name) ||
              skipFileExpenditureFileErrors(name)) {
            delete tErr[name];
          }
        });

        if (
          Object.keys({ ...stepErr, ...tErr }).length > 0 ||
          (incentiveClaimSections[i]?.title?.en === "Capital Subsidy" &&
            allErrors.capital_expenditure_details_through_bank_transactions)
        ) {
          setCurrentStep(i);
          break;
        }
      }
    } else {
      createApplicationForm(true);
    }
  };

  const calculateInvestmentAndSubsidy = (formData) => {
    const updated = { ...formData };

    // ---------------- Calculate Total Investment ----------------
    const totalInvestment = totalInvestmentFields.reduce(
      (sum, field) => sum + (parseFloat(updated[field]) || 0),
      0,
    );
    updated.total_investment = totalInvestment.toFixed(2);

    // ---------------- Calculate Total Non Eligible ----------------
    const totalNonEligible = totalNonEligibleFields.reduce(
      (sum, field) => sum + (parseFloat(updated[field]) || 0),
      0,
    );
    updated.total_non_eligible = totalNonEligible.toFixed(2);

    // ---------------- Calculate Total Eligible Investment ----------------
    const totalCapEx =
      parseFloat(updated.total_capital_expenditure_amount) || 0;
    const totalEligibleInvestment =
      //totalCapEx + totalInvestment - totalNonEligible;
      totalInvestment - totalNonEligible;
    updated.total_eligible_investment_after_non_eligible_deduction =
      totalEligibleInvestment.toFixed(2);

    // ---------------- Validation & Capital Subsidy ----------------
    const errors = {};

    // Fixed values
    const minInvestment = 10000000; // 1 Cr
    const minSubsidy = 3300000; // 33 Lakhs

    // Area-category wise max caps
    const maxCapMap = {
      A: 8000000,
      B: 12000000,
      C: 15000000,
    };

    if (totalEligibleInvestment < minInvestment) {
      updated.capital_subsidy_min_investment = "0.00";
      updated.capital_subsidy_additional_investment = "0.00";
      updated.total_max_capital_subsidy = "0.00";

      errors.total_investment = totalInvestment
        ? null
        : {
            en: "Total investment required",
            hi: "कुल निवेश आवश्यक है",
          };

      errors.total_non_eligible = totalNonEligible
        ? null
        : {
            en: "Total non-eligible required",
            hi: "कुल गैर-योग्य राशि आवश्यक है",
          };

      errors.total_eligible_investment_after_non_eligible_deduction = {
        en: "Total eligible investment must be at least 1 Crore.",
        hi: "कुल पात्र निवेश कम से कम 1 करोड़ होना चाहिए।",
      };
    } else if (!updated.area_category) {
      errors.capital_subsidy_additional_investment = {
        en: "Please select Area Category first (A/B/C).",
        hi: "कृपया पहले क्षेत्र श्रेणी चुनें (A/B/C)।",
      };
    } else {
      updated.capital_subsidy_min_investment = minSubsidy.toFixed(2);

      let additionalInvestmentSubsidy = 0;
      if (totalEligibleInvestment > minInvestment) {
        let excessAmount = totalEligibleInvestment - minInvestment;

        if (excessAmount > maxEligibleSubsidy - minInvestment) {
          excessAmount = maxEligibleSubsidy - minInvestment;
        }

        if (updated.area_category === "A")
          additionalInvestmentSubsidy = excessAmount * 0.15;
        if (updated.area_category === "B")
          additionalInvestmentSubsidy = excessAmount * 0.25;
        if (updated.area_category === "C")
          additionalInvestmentSubsidy = excessAmount * 0.3;
      }

      // Apply cap based on max capacity
      const categoryMax = maxCapMap[updated.area_category] || 0;
      const maxAdditional = categoryMax - minSubsidy;

      updated.capital_subsidy_additional_investment =
        additionalInvestmentSubsidy.toFixed(2);

      updated.total_max_capital_subsidy = (
        minSubsidy + additionalInvestmentSubsidy > categoryMax
          ? categoryMax
          : minSubsidy + additionalInvestmentSubsidy
      ).toFixed(2);

      // Clear errors
      errors.total_investment = null;
      errors.total_non_eligible = null;
      errors.total_eligible_investment_after_non_eligible_deduction = null;
      errors.capital_subsidy_additional_investment = null;
      errors.additionalInvestmentSubsidy = null;
      errors.total_max_capital_subsidy = null;
    }

    amountFieldsToConvertToWords.forEach((field) => {
      if (updated[field] !== undefined) {
        updated[`${field}_in_words`] = numberToWords(updated[field]);
      }
    });

    return { updated, errors };
  };

  const handleChange = async (e) => {
    const { name, type, value, checked, files } = e.target;
    if (!scriptRegex.test(value)) {
      // -------------------- FILE UPLOAD --------------------
      if (type === "file" && files?.length > 0) {
        const uploadedFile = files[0];
        const existingDocId = formData[name]?.id || "";
        const documentTypeId = formData[name].docTypeId || "";

        if (!documentTypeId)
          return console.error("No document_type_id mapping found for:", name);

        try {
          const fileFormData = new FormData();
          fileFormData.append("upload_doc", uploadedFile);
          fileFormData.append("upload_date", formatDate(new Date()));
          fileFormData.append("category_id", 1);
          fileFormData.append("document_type_id", documentTypeId);
          fileFormData.append("application_id", applicationId);
          fileFormData.append("upload_document_id", existingDocId);
          fileFormData.append("user_id", userDetails?.user_details?.id);

          const uploadResponse = await api.post(
            "/application/document/upload",
            fileFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );

          if (uploadResponse?.success && uploadResponse?.data?.path) {
            setFormData((prev) => ({
              ...prev,
              [name]: {
                url: uploadResponse.data.path,
                id: uploadResponse.data.id,
                name: uploadedFile.name,
                docTypeId: documentTypeId,
              },
            }));
            setErrors((prev) => ({ ...prev, [name]: null }));
            setPendingDocIds((prev) =>
              prev.filter((id) => id !== String(documentTypeId)),
            );
          }
        } catch (err) {
          console.log(err);
          setErrors((prev) => ({
            ...prev,
            [name]: {
              en: "File upload failed. Please try again.",
              hi: "फ़ाइल अपलोड विफल। कृपया पुनः प्रयास करें।",
            },
          }));
        }

        return;
      }

      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        let newData = { ...updated };

        if (employmentFields.includes(name)) {
          const calculated = calculateEmploymentFields(updated);
          if (calculated.bonafied_employees_percentage > 70) {
            setErrors((prev) => ({
              ...prev,
              bonafied_employees_percentage: null,
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              bonafied_employees_percentage: {
                en: "Bonafide Employees Percentage must be at least 70%",
                hi: "वास्तविक कर्मचारी % कम से कम 70% होना चाहिए।",
              },
            }));
          }
          newData = { ...newData, ...calculated };
        }

        return newData;
      });

      if (type === "checkbox" && name === "use_same_details_as_term_loan") {
        setFormData((prev) => {
          let updated = { ...prev, [name]: checked };

          if (
            checked &&
            Array.isArray(prev.term_loan_details) &&
            prev.term_loan_details.length > 0
          ) {
            const firstRow = prev.term_loan_details[0];

            updated = {
              ...updated,
              subsidy_ifsc_code: firstRow.ifsc_code || "", // assuming IFSC is part of row later
              subsidy_bank_name: firstRow.bank_name || "",
              subsidy_branch_name: firstRow.branch_name || "",
              subsidy_bank_account_number: firstRow.loan_account_number || "",
            };
          }

          return updated;
        });

        setErrors((prev) => ({
          ...prev,
          subsidy_ifsc_code: "",
          subsidy_bank_name: "",
          subsidy_branch_name: "",
          subsidy_bank_account_number: "",
        }));
        return; // ✅ stop here, no need to run rest of handleChange
      }

      if (name === "land_district") {
        const selectedDistrictId = uttarakhandDistrictList.find(
          (d) => d.label === value,
        )?.id;

        setFormData((prev) => ({
          ...prev,
          land_district: value,
          land_tehsil: "", // reset tehsil
        }));

        const options = tehsilMaster
          .filter((t) => String(t.district_id) === String(selectedDistrictId))
          .map((t) => ({
            label: t.name,
            value: t.name,
            hi: t.name_hi,
          }));

        setTehsilOptions(options);

        return;
      }

      if (name === "project_type") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          nic_code: "", // reset NIC Code if project type changes
          tourism_product_name: "", // reset tourism product
        }));

        // Restrict NIC options if Expansion
        if (value === "Expansion") {
          setNicOptions([{ label: "55101", value: "55101", hi: "55101" }]);

          // 🔹 OPTIONAL: auto-select 55101 for user convenience
          setFormData((prev) => ({
            ...prev,
            [name]: value,
            nic_code: "55101",
            tourism_product_name: "",
          }));
        } else {
          setNicOptions(allNicOptions); // fallback to all options
        }

        return; // ✅ stop here so rest of handleChange doesn't overwrite
      }

      // -------------------- NON-FILE FIELDS --------------------
      setFormData((prev) => {
        let updated = {
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        };

        if (amountFieldsToConvertToWords.includes(name)) {
          updated[`${name}_in_words`] = numberToWords(value);
        }

        if (name === "nic_code") {
          const products = nicCodeToTourismProducts[value] || [];
          setTourismOptions(products);
          updated.tourism_product_name = "";
        }

        if (name === "land_type") {
          landTypeDynamicFields.forEach((field) => (updated[field] = ""));
        }

        if (sourceFinanceCheckBoxTargets[name]) {
          sourceFinanceCheckBoxTargets[name].forEach((targetField) => {
            updated[targetField] = "";
          });
        }

        if (calculationFields.includes(name)) {
          const { updated: calcUpdated, errors: calcErrors } =
            calculateInvestmentAndSubsidy(updated);
          setErrors((prev) => ({ ...prev, ...calcErrors }));
          updated = calcUpdated;
        }

        return updated;
      });

      setErrors((prev) => ({ ...prev, [name]: null }));

      if (financeCheckboxes.includes(name)) {
        const isValid = validateFinanceCheckboxes({
          ...formData,
          [name]: type === "checkbox" ? checked : value,
        });

        setErrors((prev) => ({
          ...prev,
          term_loan: isValid
            ? null
            : {
                en: "Please select at least one source of finance.",
                hi: "कृपया कम से कम एक वित्त स्रोत चुनें।",
              },
        }));
      }
    }
  };

 const skipFileExpenditureFileErrors = (name) => {
    return (
      (name === "total_capital_expenditure_amount" ||
        name.toLowerCase().startsWith("capital_expenditure_details_through_bank_transactions")) &&
      !!formData?.capital_expenditure_details_excel?.url?.trim()
    );
  };

  const skipLandTypeErrors = (name) => {
    const landType = formData?.land_type;
    if (
      landFieldGroups.purchased.includes(name) &&
      landType !== LAND_TYPES.PURCHASED
    ) {
      return true;
    }
    if (
      landFieldGroups.leaseOrGovt.includes(name) &&
      ![LAND_TYPES.LEASE_OR_RENTAL, LAND_TYPES.GOVERNMENT_ALLOTTED].includes(
        landType,
      )
    ) {
      return true; // skip validation
    }
    return false;
  };

  const skipInsertAssistanceDocuments = (name) => {
    if (
      insertAssistanceDocuments.includes(name) &&
      String(financialIncentiveClaim) !==
        incentiveClaimTypes.interestassistance.value
    ) {
      return true;
    }
    return false;
  };

  const skipCapitalSubsidySecondInstallmentDocuments = (name) => {
    if (!capitalSubsidySecondInstallmentDocuments.includes(name)) return false;

    // If it's capital incentive AND second installment → do NOT skip
    if (
      String(financialIncentiveClaim) ===
        incentiveClaimTypes.sreimbursementcapitalincentive.value &&
      installmentType === installmentsTypes.second.value
    ) {
      return false;
    }

    // Skip otherwise
    return true;
  };

  const matchesInterestField = (name, fieldArray) => {
    return fieldArray.some((key) => name.startsWith(key));
  };

  const skipInterestSubsidyInstallmentErrors = (name) => {
    const isFirst = matchesInterestField(
      name,
      insterstSubsidyBankDetailsFieldsFirstInstallment,
    );
    const isSecond = matchesInterestField(
      name,
      insterstSubsidyBankDetailsFieldsSecondInstallment,
    );
    const isThird = matchesInterestField(
      name,
      insterstSubsidyBankDetailsFieldsThirdInstallment,
    );

    if (
      (isFirst || isSecond || isThird) &&
      String(financialIncentiveClaim) !==
        incentiveClaimTypes.interestassistance.value
    ) {
      return true;
    }

    if (
      (isSecond || isThird) &&
      String(financialIncentiveClaim) ===
        incentiveClaimTypes.interestassistance.value &&
      installmentType === installmentsTypes.first.value
    ) {
      return true;
    }

    if (
      isThird &&
      String(financialIncentiveClaim) ===
        incentiveClaimTypes.interestassistance.value &&
      installmentType === installmentsTypes.second.value
    ) {
      return true;
    }

    return false;
  };

  const skipGenderValidation = (name) => {
    // Only apply skip logic to the gender field
    if (name !== "gender") return false;

    const nature = formData?.nature_of_organization;

    // If gender is not visible → skip
    const isVisible =
      nature === "Proprietorship Firm" || nature === "One Person Company";

    if (!isVisible) {
      return true; // skip validation
    }

    return false; // visible → do NOT skip
  };

  const skipFinanceErrors = (fieldName, formData) => {
    // Loop over all sourceFinanceTargets
    return Object.entries(sourceFinanceCheckBoxTargets).some(
      ([checkbox, targets]) => {
        // If the field is a target
        if (targets.includes(fieldName)) {
          // Skip validation if controlling checkbox is not checked
          return (
            formData[checkbox] === false ||
            formData[checkbox] === "0" ||
            !formData[checkbox]
          );
        }
        return false;
      },
    );
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    const { errors: stepErrors } = validateSingleSectionFormData(
      incentiveClaimSections[currentStep]?.data || [],
      { ...formData, [name]: value },
    );

    // 🔹 Skip land type errors here too
    if (skipLandTypeErrors(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: null, // clear any previous error
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: stepErrors[name] || "",
    }));
  };

  const handleTableChange = async (
    tableName,
    updatedRows,
    newRow = false,
    rowIndex = null,
    fieldName = null,
    rowRemoved = false,
  ) => {
    let newFormDataUpdate = { [tableName]: updatedRows };

    // --- Special case: khasra_raqwa_number ---
    if (tableName === "kharsa_raqwa_number") {
      updatedRows = updatedRows.map((row) => {
        let areaInSqm = row.area_in_sqm || "";

        if (row.raqwa_in_hectares) {
          const hectares = parseFloat(row.raqwa_in_hectares);
          if (!isNaN(hectares)) {
            areaInSqm = (hectares * 10000).toFixed(2);
          } else {
            areaInSqm = "";
          }
        } else {
          areaInSqm = "";
        }
        return { ...row, area_in_sqm: areaInSqm };
      });

      const totalSqm = updatedRows.reduce(
        (sum, row) => sum + (parseFloat(row.area_in_sqm) || 0),
        0,
      );

      newFormDataUpdate = {
        [tableName]: updatedRows,
        total_land_in_sqm: totalSqm.toFixed(2),
      };

      setErrors((prev) => {
        const newErrors = { ...prev };

        updatedRows.forEach((row, idx) => {
          const key = `${tableName}.${idx}.area_in_sqm`;

          if (!row.area_in_sqm) {
            newErrors[key] = {
              en: "Area in Sqm is required.",
              hi: "क्षेत्रफल (Sqm) आवश्यक है।",
            };
          } else {
            delete newErrors[key];
          }
        });

        if (!totalSqm || isNaN(totalSqm)) {
          newErrors["total_land_in_sqm"] = {
            en: "Total area in Sqm is required.",
            hi: "कुल क्षेत्रफल (Sqm) आवश्यक है।",
          };
        } else {
          delete newErrors["total_land_in_sqm"];
        }

        return newErrors;
      });
    }

    // --- Interest Subsidy Loan & Interest Details Calculation ---
    if (
      tableName ===
        "loan_interest_details_interest_subsidy_first_installment" ||
      tableName ===
        "loan_interest_details_interest_subsidy_second_installment" ||
      tableName === "loan_interest_details_interest_subsidy_third_installment"
    ) {
      updatedRows = updatedRows.map((row, idx) => {
        const startDate = row.interest_start_date;
        const endDate = row.interest_end_date;
        const principal = parseFloat(row.principal_paid) || 0;
        const interest = parseFloat(row.interest_paid) || 0;
        const panel = parseFloat(row.panel_amount) || 0;

        let rowErrors = {};

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);

          if (!isNaN(start) && !isNaN(end)) {
            if (end < start) {
              rowErrors.interest_end_date = {
                en: "End Date must be after Start Date.",
                hi: "समाप्ति तिथि शुरूआती तिथि के बाद होनी चाहिए।",
              };
            } else {
              rowErrors.interest_end_date = null;
            }
          }
        }

        // ... your existing validations ...

        // --- TOTAL INTEREST CALCULATION ---
        const totalInterest = interest - panel;

        // --- CLEAR total_interest IF VALID ---
        if (!isNaN(totalInterest) && totalInterest >= 0) {
          rowErrors.total_interest = null;
        } else {
          rowErrors.total_interest = {
            en: "Total Interest cannot be negative.",
            hi: "कुल ब्याज ऋणात्मक नहीं हो सकता।",
          };
        }

        // --- SAVE ERRORS FOR ROW ---
        setErrors((prev) => {
          const updated = { ...prev };

          Object.entries(rowErrors).forEach(([key, value]) => {
            const fullKey = `${tableName}.${idx}.${key}`;

            if (value === null) {
              delete updated[fullKey];
            } else {
              updated[fullKey] = value;
            }
          });

          return updated;
        });

        return {
          ...row,
          total_interest: !isNaN(totalInterest) ? totalInterest.toFixed(2) : "",
        };
      });

      // --- TOTAL TABLE INTEREST CALCULATION ---
      const totalInterestPaid = updatedRows.reduce(
        (sum, row) => sum + (parseFloat(row.total_interest) || 0),
        0,
      );

      let totalFieldName = "";
      let eligibleFieldName = "";

      if (tableName.includes("first")) {
        totalFieldName = "total_interest_paid_first_installment";
        eligibleFieldName = "maximum_eligible_amount_first_installment";
      } else if (tableName.includes("second")) {
        totalFieldName = "total_interest_paid_second_installment";
        eligibleFieldName = "maximum_eligible_amount_second_installment";
      } else if (tableName.includes("third")) {
        totalFieldName = "total_interest_paid_third_installment";
        eligibleFieldName = "maximum_eligible_amount_third_installment";
      }

      const category = formData?.area_category; // A / B / C
      const maxLimits = { A: 400000, B: 500000, C: 600000 };
      const maxAllowed = maxLimits[category] || 0;

      let categoryError = null;

      if (totalInterestPaid > maxAllowed) {
        categoryError = {
          en: `Exceeds maximum allowed limit ${
            numberToWords(maxAllowed).en
          } for Category ${category}.`,
          hi: `श्रेणी ${category} के लिए अधिकतम सीमा ${
            numberToWords(maxAllowed).hi
          } से अधिक है।`,
        };
      }

      // Set table-level totals without resetting the value
      newFormDataUpdate = {
        [tableName]: updatedRows,
        [totalFieldName]: totalInterestPaid.toFixed(2), // keep original
        [`${totalFieldName}_in_words`]: numberToWords(totalInterestPaid),
      };

      setErrors((prev) => {
        const updated = { ...prev };

        if (categoryError) {
          updated[totalFieldName] = categoryError;
        } else {
          delete updated[totalFieldName];
        }

        return updated;
      });
    }

    if (tableName === "capital_expenditure_details_through_bank_transactions") {
      const totalAmount = updatedRows.reduce(
        (sum, row) => sum + (parseFloat(row.amount_of_payment) || 0),
        0,
      );

      newFormDataUpdate = {
        [tableName]: updatedRows,
        total_capital_expenditure_amount: totalAmount.toFixed(2),
      };

      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.total_capital_expenditure_amount;
        return updated;
      });

      setFormData((prev) => {
        const { updated: calcUpdated, errors: calcErrors } =
          calculateInvestmentAndSubsidy({
            ...prev,
            ...newFormDataUpdate,
          });

        setErrors((prev) => ({ ...prev, ...calcErrors }));
        return calcUpdated;
      });
    }

    if (shareholdingTables[tableName]) {
      const fieldName = shareholdingTables[tableName];

      const totalShareholding = updatedRows.reduce(
        (sum, row) => sum + (parseFloat(row[fieldName]) || 0),
        0,
      );

      if (totalShareholding !== 100) {
        setErrors((prev) => {
          const newErrors = { ...prev };

          Object.keys(newErrors).forEach((key) => {
            if (
              key.startsWith(`${tableName}.`) &&
              key.endsWith(`.${fieldName}`)
            ) {
              delete newErrors[key];
            }
          });

          updatedRows.forEach((_, rowIndex) => {
            newErrors[`${tableName}.${rowIndex}.${fieldName}`] = {
              en: "Total shareholding must be 100%.",
              hi: "कुल शेयरहोल्डिंग 100% होना चाहिए।",
            };
          });

          return newErrors;
        });
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };

          Object.keys(newErrors).forEach((key) => {
            if (
              key.startsWith(`${tableName}.`) &&
              key.endsWith(`.${fieldName}`)
            ) {
              delete newErrors[key];
            }
          });

          return newErrors;
        });
      }
    }

    if (sourceFinanceTableAmountTargets[tableName]) {
      const { field, target } = sourceFinanceTableAmountTargets[tableName];
      if (fieldName === field || rowRemoved) {
        const total = updatedRows.reduce(
          (sum, row) => sum + (parseFloat(row[field]) || 0),
          0,
        );
        newFormDataUpdate[target] = total.toFixed(2);

        setErrors((prev) => ({
          ...prev,
          [target]: { en: null, hi: null },
        }));
      }

      if (tableName === "term_loan_details") {
        setErrors((prev) => {
          const newErrors = { ...prev };

          updatedRows.forEach((row, idx) => {
            const sanctionDate = row.sanction_date
              ? new Date(row.sanction_date)
              : null;
            const firstInstalmentDate = row.first_instalment_disbursal_date
              ? new Date(row.first_instalment_disbursal_date)
              : null;

            const key = `${tableName}.${idx}.first_instalment_disbursal_date`;

            if (
              sanctionDate &&
              firstInstalmentDate &&
              firstInstalmentDate < sanctionDate
            ) {
              newErrors[key] = {
                en: "First Instalment Disbursal Date must be after Sanction Date.",
                hi: "पहली किस्त की तारीख, स्वीकृति तारीख के बाद होनी चाहिए।",
              };
            } else {
              delete newErrors[key];
            }
          });

          return newErrors;
        });
      }
    }

    // --- Stamp Duty Totals ---
    if (tableName === "stamp_duty_for_first_installment_details") {
      if (rowRemoved) {
        const totalStampDuty = updatedRows.reduce(
          (sum, row) => sum + (parseFloat(row.stamp_duty) || 0),
          0,
        );

        newFormDataUpdate = {
          [tableName]: updatedRows,
          total_stamp_duty_per_year: totalStampDuty.toString(),
        };
      }

      if (fieldName === "pde_no") {
        const enteredPDE = updatedRows[rowIndex]?.pde_no || "";

        // Helper: reset row fields (keep pde_no intact)
        const resetRow = () => ({
          ...updatedRows[rowIndex],
          district: "",
          location: "",
          khasra: "",
          khatoni: "",
          buyer: "",
          seller: "",
          stamp_duty: "",
          regdate: "",
        });

        // Helper: calculate total stamp duty
        const calculateTotalStampDuty = (rows) =>
          rows.reduce((sum, row) => sum + (parseFloat(row.stamp_duty) || 0), 0);

        // Helper: check duplicate PDE in other rows
        const isDuplicatePDE = (pde) =>
          updatedRows.some(
            (row, idx) => idx !== rowIndex && row.pde_no?.trim() === pde.trim(),
          );

        // 🔹 Case 1: PDE < 15 → reset immediately
        if (enteredPDE.length < 15) {
          const newRows = [...updatedRows];
          newRows[rowIndex] = resetRow();

          const totalStampDuty = calculateTotalStampDuty(newRows);

          newFormDataUpdate = {
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          };

          setFormData((prev) => ({
            ...prev,
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          }));

          setErrors((prev) => ({
            ...prev,
            [`${tableName}.${rowIndex}.pde_no`]: {
              en: "PDE number must be at least 15 digits.",
              hi: "PDE नंबर कम से कम 15 अंकों का होना चाहिए।",
            },
          }));

          return;
        }

        // 🔹 Case 2: Check duplicate PDE before API call
        if (isDuplicatePDE(enteredPDE)) {
          const newRows = [...updatedRows];
          newRows[rowIndex] = resetRow();

          const totalStampDuty = calculateTotalStampDuty(newRows);

          newFormDataUpdate = {
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          };

          setFormData((prev) => ({
            ...prev,
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          }));

          setErrors((prev) => ({
            ...prev,
            [`${tableName}.${rowIndex}.pde_no`]: {
              en: "This PDE number already exists in another row.",
              hi: "यह PDE नंबर पहले से किसी अन्य पंक्ति में मौजूद है।",
            },
          }));

          return;
        }

        // 🔹 Case 3: PDE ≥ 15 → call API
        // 🔹 Case 3: PDE ≥ 15 → call API
        try {
          setLoadingPDE(true);
          const response = await api.post("/tourism-details", {
            Pde_id: enteredPDE,
          });

          const data = response?.data;

          if (data && data.District) {
            // ✅ valid data → populate row
            const newRows = [...updatedRows];
            newRows[rowIndex] = {
              ...newRows[rowIndex],
              district: data.District || "",
              location: data.location || "",
              khasra: data.khasra || "",
              khatoni: data.khatoni || "",
              buyer: data.Buyer || "",
              seller: data.Seller || "",
              stamp_duty: String(data.StampDuty) || "",
              regdate: data.regdate || "",
            };

            const totalStampDuty = calculateTotalStampDuty(newRows);

            newFormDataUpdate = {
              [tableName]: newRows,
              total_stamp_duty_per_year: totalStampDuty.toString(),
            };

            setFormData((prev) => ({
              ...prev,
              [tableName]: newRows,
              total_stamp_duty_per_year: totalStampDuty.toString(),
            }));

            // ✅ clear row-specific errors
            setErrors((prev) => {
              const newErrors = { ...prev };
              Object.keys(newErrors).forEach((key) => {
                if (key.startsWith(`${tableName}.${rowIndex}.`)) {
                  delete newErrors[key];
                }
              });
              return newErrors;
            });
          } else {
            // ❌ invalid response → reset row
            const newRows = [...updatedRows];
            newRows[rowIndex] = resetRow();

            const totalStampDuty = calculateTotalStampDuty(newRows);

            newFormDataUpdate = {
              [tableName]: newRows,
              total_stamp_duty_per_year: totalStampDuty.toString(),
            };

            setFormData((prev) => ({
              ...prev,
              [tableName]: newRows,
              total_stamp_duty_per_year: totalStampDuty.toString(),
            }));

            setErrors((prev) => ({
              ...prev,
              [`${tableName}.${rowIndex}.pde_no`]: {
                en: "Invalid PDE number.",
                hi: "अमान्य PDE नंबर।",
              },
            }));

            return;
          }
        } catch (err) {
          const newRows = [...updatedRows];
          newRows[rowIndex] = resetRow();

          const totalStampDuty = calculateTotalStampDuty(newRows);

          newFormDataUpdate = {
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          };

          setFormData((prev) => ({
            ...prev,
            [tableName]: newRows,
            total_stamp_duty_per_year: totalStampDuty.toString(),
          }));

          setErrors((prev) => ({
            ...prev,
            [`${tableName}.${rowIndex}.pde_no`]: {
              en: "Failed to fetch PDE details.",
              hi: "PDE विवरण प्राप्त करने में विफल।",
            },
          }));
        } finally {
          setLoadingPDE(false);
          return;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      ...newFormDataUpdate,
    }));

    if (!newRow && rowIndex !== null && fieldName) {
      const fieldConfig = incentiveClaimSections[currentStep]?.data
        ?.flatMap((section) => section.fields)
        ?.find((field) => field.inputProps?.name === tableName)
        ?.inputProps?.headers?.find((h) => h.name === fieldName);

      let error = null;

      if (
        fieldConfig?.validation?.required &&
        !updatedRows[rowIndex][fieldName]
      ) {
        error = fieldConfig.validation.message || {
          en: "This field is required.",
          hi: "यह फ़ील्ड आवश्यक है।",
        };
      }

      if (
        !error &&
        fieldConfig?.validation?.pattern &&
        !fieldConfig.validation.pattern.test(updatedRows[rowIndex][fieldName])
      ) {
        error = fieldConfig.validation.message || {
          en: "Invalid field value.",
          hi: "फ़ील्ड मान्य नहीं है।",
        };
      }

      setErrors((prev) => {
        const key = `${tableName}.${rowIndex}.${fieldName}`;

        const hasInterestSubsidyTableError = Object.keys(prev).some((errKey) =>
          errKey.startsWith("loan_interest_details_interest_subsidy"),
        );

        if (hasInterestSubsidyTableError) {
          return prev;
        }

        if (prev[key] && prev[key].en?.includes("Total shareholding")) {
          return prev;
        }

        if (
          prev[key] &&
          prev[key].en?.includes("First Instalment Disbursal Date")
        ) {
          return prev;
        }

        return { ...prev, [key]: error };
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const validateTableFields = (fields, formData) => {
    let errors = {};
    let isValid = true;

    fields.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.inputProps.type === "table") {
          const tableName = field.inputProps.name;

          if (skipInterestSubsidyInstallmentErrors(tableName) || 
              skipFileExpenditureFileErrors(tableName)) {
            return; // skip validation for this table
          }

          // ✅ Skip validation if controlling checkbox is unchecked
          const controllingCheckbox = Object.entries(
            sourceFinanceCheckBoxTargets,
          ).find(([checkbox, targets]) => targets.includes(tableName))?.[0];

          if (controllingCheckbox) {
            const checkboxValue = formData[controllingCheckbox];
            const isChecked =
              checkboxValue === true ||
              checkboxValue === "1" ||
              checkboxValue === 1;

            if (!isChecked) {
              return; // skip this table entirely
            }
          }

          const rows = formData[tableName] || [];
          const headers = field.inputProps.headers || [];

          if (rows.length === 0) {
            // validate at least one row template
            headers.forEach((header) => {
              if (header.validation?.required) {
                const labelEn = header.label?.en || header.label || header.name;
                const labelHi = header.label?.hi || header.label || header.name;

                errors[`${tableName}.0.${header.name}`] = {
                  en: header.validation.message?.en || `${labelEn} is required`,
                  hi: header.validation.message?.hi || `${labelHi} आवश्यक है`,
                };

                isValid = false;
              }
            });
          }

          rows.forEach((rowData, rowIndex) => {
            headers.forEach((header) => {
              const cellValue = rowData?.[header.name] || "";
              const rules = header.validation || {};

              const labelEn = header.label?.en || header.label || header.name;
              const labelHi = header.label?.hi || header.label || header.name;

              if (rules.required && !cellValue.trim()) {
                errors[`${tableName}.${rowIndex}.${header.name}`] = {
                  en: rules.message?.en || `${labelEn} is required`,
                  hi: rules.message?.hi || `${labelHi} आवश्यक है`,
                };
                isValid = false;
              }

              if (
                rules.pattern instanceof RegExp &&
                cellValue &&
                !rules.pattern.test(cellValue)
              ) {
                errors[`${tableName}.${rowIndex}.${header.name}`] = {
                  en: rules.message?.en || `${labelEn} is invalid`,
                  hi: rules.message?.hi || `${labelHi} अमान्य है`,
                };
                isValid = false;
              }
            });
          });

          if (shareholdingTables[tableName]) {
            const fieldName = shareholdingTables[tableName];
            const total = rows.reduce(
              (sum, row) => sum + (parseFloat(row[fieldName]) || 0),
              0,
            );

            if (total !== 100) {
              rows.forEach((_, rowIndex) => {
                errors[`${tableName}.${rowIndex}.${fieldName}`] = {
                  en: "Total shareholding must be 100%.",
                  hi: "कुल शेयरहोल्डिंग 100% होनी चाहिए।",
                };
              });
              isValid = false;
            }
          }
        }
      });
    });

    return { errors, isValid };
  };

  const fields = incentiveClaimSections[currentStep]?.data || [];

  /** ---------------- RENDER ---------------- **/

  // const dynamicTabs = [
  //   { key: "step-0", label: "CAF Info" },
  //   ...incentiveClaimSections
  //     .map((section, i) => ({
  //       key: `step-${i + 1}`,
  //       label: section.title,
  //       section,
  //     }))
  //     .filter(
  //       (section) =>
  //         !(
  //           String(financialIncentiveClaim) ===
  //             schemeTypes.interestassistance.value &&
  //           section.label.includes("Capital Subsidy")
  //         )
  //     ),
  // ];

  // const [activeTab, setActiveTab] = useState(dynamicTabs[0].key);

  const isInterest =
    String(financialIncentiveClaim) === schemeTypes.interestassistance.value;
  const isReimbursement =
    String(financialIncentiveClaim) ===
    schemeTypes.sreimbursementcapitalincentive.value;

  const isFirstInstallment = installmentType === installmentsTypes.first.value;
  const isSecondInstallment =
    installmentType === installmentsTypes.second.value;
  const isThirdInstallment = installmentType === installmentsTypes.third.value;

  const tabs = incentiveClaimSections
    .map((section, index) => {
      let title = section.title; // {en:"", hi:""}

      if (!title) return null;

      let newTitle = { ...title }; // keep object structure

      if (isInterest) {
        newTitle = {
          en: title.en.replace("Stamp Duty", "Subsidy Details"),
          hi: title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण"),
        };
      } else if (isReimbursement && isSecondInstallment) {
        newTitle = {
          en: title.en.replace("Stamp Duty", "Subsidy Details"),
          hi: title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण"),
        };
      }

      return {
        key: `step-${index}`,
        label: newTitle, // 👈 object label
        index,
      };
    })
    .filter(Boolean)
    .filter(
      (section) =>
        !(isReimbursement && section.label.en.includes("Interest Subsidy")),
    );

  const isDataReady =
    financialIncentiveClaim !== null &&
    installmentType !== null &&
    applicationStatus !== null;

  return (
    <>
      <div className="w-full">
        {apiMessage && (
          <Message
            apiMessage={apiMessage}
            setApiMessage={setApiMessage}
          ></Message>
        )}
        {/* Tabs */}
        {!isDataReady ? (
          <div className="p-4">Loading...</div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 pb-4 sm:pt-6 sm:pb-6 pr-4 sm:pr-6 pl-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`px-4 py-2 rounded ${
                  currentStep === tab.index
                    ? "bg-btn text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => setCurrentStep(tab.index)}
              >
                {language ? tab.label?.hi : tab.label?.en}
              </button>
            ))}
          </div>
        )}

        {/* Form Section */}
        <IncentiveClaimFormSection language={language}>
          <div className="flex flex-wrap mx-2">
            {isFormSubmitted ? (
              <div className="flex justify-center items-center min-h-[70vh] w-full">
                <div className="flex flex-col items-center rounded-md p-10 shadow-md bg-white max-w-xl text-center">
                  <div className="mb-4 text-green-600">
                    <CheckIcon className="w-16 h-16" fill="#018f9b" />
                  </div>

                  <h5 className="text-xl font-semibold text-theme-color">
                    {language ? "सबमिशन सफल!" : "Submission Successful!"}
                  </h5>

                  <p className="mt-2 text-gray-600">
                    {language
                      ? "आवेदन सफलतापूर्वक जमा हो गया है। हमारे साथ जुड़े रहने के लिए धन्यवाद।"
                      : "The Application has been submitted successfully! Thanks for being there with us."}
                  </p>

                  <div className="mt-6">
                    <Button
                      className="px-6 py-2 bg-btn text-white rounded-md shadow user-theme-btn"
                      onClick={() => navigate("/dashboard")}
                    >
                      {language ? "डैशबोर्ड" : "Dashboard"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* CAF Info */}
                {currentStep === 0 && (
                  <div className="w-full flex flex-wrap items-center justify-between">
                    <div className="w-full lg:w-1/2 mb-4 px-2">
                      <span className="text-lg font-semibold block w-full">
                        {language ? "सीएएफ आईडी:" : "CAF ID:"}
                      </span>
                      <span>{formData.caf_id || "N/A"}</span>
                    </div>
                    <div className="w-full lg:w-1/2 mb-4 px-2 text-end">
                      <span className="text-lg font-semibold block w-full">
                        {language
                          ? "सीएएफ मंजूरी तारीख:"
                          : "CAF Approval Date:"}
                      </span>
                      <span>
                        {userDetails?.updated_date ||
                          (language ? "उपलब्ध नहीं है" : "N/A")}
                      </span>
                    </div>
                  </div>
                )}

                {/* Dynamic Sections */}
                {incentiveClaimSections[currentStep]?.data
                  ?.filter((section) => {
                    // Hide "Tax Documents" for first installment
                    if (
                      installmentType === installmentsTypes.first.value &&
                      section.title.en === "Tax Documents"
                    ) {
                      return false;
                    }

                    // Hide "Stamp Duty" & "Stamp Duty Documents" for second & third installments
                    if (
                      (installmentType === installmentsTypes.second.value ||
                        installmentType === installmentsTypes.third.value ||
                        String(financialIncentiveClaim) ===
                          incentiveClaimTypes.interestassistance.value) &&
                      ["Stamp Duty", "Stamp Duty Documents"].includes(
                        section.title.en,
                      )
                    ) {
                      return false;
                    }

                    return true; // Keep other sections
                  })
                  .map((section, index) => (
                    <IncentiveClaimFormSubSection
                      key={index}
                      sectionHeading={
                        language ? section.title.hi : section.title.en
                      }
                      sectionFields={section.fields}
                      formData={formData}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      handleTableChange={handleTableChange}
                      errors={errors}
                      setFormData={setFormData}
                      tourismOptions={tourismOptions}
                      nicOptions={nicOptions}
                      tehsilOptions={tehsilOptions}
                      pendingDocIds={pendingDocIds}
                      investClaimId={financialIncentiveClaim}
                      installmentType={installmentType}
                      language={language}
                      applicationId={applicationId}
                    />
                  ))}

                {/* Declaration on last step */}
                {currentStep === incentiveClaimSections.length - 1 && (
                  <div className="w-full px-2 mb-6 mt-4">
                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        checked={declarationChecked}
                        onChange={(e) => {
                          setDeclarationChecked(e.target.checked);
                          setDeclarationError("");
                        }}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-700">
                        {language
                          ? infoTexts.declaration.hi
                          : infoTexts.declaration.en}
                      </span>
                    </label>

                    {declarationError && (
                      <p className="text-red-600 text-sm mt-2">
                        {language ? declarationError.hi : declarationError.en}
                      </p>
                    )}

                    <p className="text-red-600 text-sm mt-3">
                      {language
                        ? infoTexts.tentativeIncentiveNote.hi
                        : infoTexts.tentativeIncentiveNote.en}
                    </p>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="w-full flex justify-between px-2 mt-4">
                  {currentStep > 0 ? (
                    <Button
                      className="bg-btn px-6 py-2 rounded"
                      onClick={handleBack}
                    >
                      {language ? "पीछे" : "Back"}
                    </Button>
                  ) : (
                    <div />
                  )}

                  <Button
                    className="bg-btn px-6 py-2 rounded"
                    onClick={() => {
                      currentStep === incentiveClaimSections.length - 1
                        ? handleSubmit()
                        : handleNext();
                    }}
                  >
                    {currentStep === incentiveClaimSections.length - 1
                      ? language
                        ? "जमा करें"
                        : "Submit"
                      : language
                        ? "अगला"
                        : "Next"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </IncentiveClaimFormSection>
      </div>

      <div className="relative">
        {/* form contents go here */}

        {loadingPDE && <LoaderOverlay />}
      </div>
    </>
  );
};

export default IncentiveClaimForm;
